import { Check, Clock, GitMerge, type LucideIcon, RotateCcw, Users, X } from 'lucide-react'
import { type RefObject, useEffect, useId, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/utils/cn'
import { subjectTag } from '../format'
import { useStableCallback } from '../questioning/use-stable-callback'
import { ENTER_UP, MICRO_LABEL, MONO, ON_PRIMARY } from '../recipes'
import type { TattooPair } from '../types'
import {
  type MarkOwner,
  type PairDecisionActions,
  type PairOutcome,
  pairOutcome,
  type TattooDecision,
} from './tattoo-match-model'

/** Keys that decide a pair: S / J = same, D / K = different (left and right hand). */
export const DECISION_KEYS: Record<TattooDecision, readonly string[]> = {
  same: ['s', 'j'],
  different: ['d', 'k'],
}

/** The longest decision note the API takes (`NOTE_MAX_LENGTH` of the tattoo matches). */
const NOTE_MAX_LENGTH = 1000

interface DecisionDraft {
  note: string
  onNoteChange: (note: string) => void
}

export interface PairDecisionPanelProps {
  pair: TattooPair
  owners: { query: MarkOwner; candidate: MarkOwner }
  /** Omitted for a viewer: the panel only states where the pair stands. */
  actions?: PairDecisionActions
}

/**
 * Decision block of the pair view: "Same tattoo" / "Different tattoo" with a note and the S / D
 * keys, then the merge proposal for a confirmed pair on two subjects. It must sit inside the
 * shell's `@container/humint`: under `@5xl/humint` (a phone, a tablet) it is read-only and says
 * the analyst decides on a desktop. The shell's breakpoint, not the pair view's own width, so a
 * laptop's narrower pair view still decides. Mount it per pair: the note starts empty.
 */
export function PairDecisionPanel({ pair, owners, actions }: PairDecisionPanelProps) {
  const [note, setNote] = useState('')
  const oneSubject = owners.query.id === owners.candidate.id
  const outcome = pairOutcome(pair, !oneSubject)
  if (!actions) return <ReadOnlyStatus outcome={outcome} reason="role" />
  // A decided pair whose two marks one subject holds is never reopened (409 same_owner).
  const onReopen = oneSubject ? undefined : () => actions.onReopen(pair.id)
  return (
    <>
      <ReadOnlyStatus outcome={outcome} reason="phone" className="@5xl/humint:hidden" />
      <div className="hidden flex-col gap-3 @5xl/humint:flex">
        {outcome === 'pending' && (
          <DecideForm
            pair={pair}
            owners={owners}
            actions={actions}
            draft={{ note, onNoteChange: setNote }}
          />
        )}
        {outcome === 'mergeProposal' && (
          <MergeProposal pairId={pair.id} owners={owners} actions={actions} />
        )}
        {outcome !== 'pending' && outcome !== 'mergeProposal' && (
          <DecidedLine outcome={outcome} onReopen={onReopen} busy={actions.busy} />
        )}
      </div>
    </>
  )
}

const OUTCOME_ICON: Record<PairOutcome, LucideIcon> = {
  pending: Clock,
  different: X,
  same: Check,
  sameSeenTogether: Users,
  motif: Users,
  mergeProposal: GitMerge,
}

function ReadOnlyStatus({
  outcome,
  reason,
  className,
}: {
  outcome: PairOutcome
  reason: 'role' | 'phone'
  className?: string
}) {
  const { t } = useTranslation()
  const Icon = OUTCOME_ICON[outcome]
  return (
    <div className={cn('flex flex-col gap-1.5 rounded-xl border border-border p-3', className)}>
      <span className="flex items-center gap-2 text-sm font-medium">
        <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        {t(`humint.tattooMatch.outcome.${outcome}`)}
      </span>
      <span className="text-xs text-muted-foreground">
        {t(`humint.tattooMatch.decision.readOnly.${reason}`)}
      </span>
    </div>
  )
}

interface DecideFormProps {
  pair: TattooPair
  owners: PairDecisionPanelProps['owners']
  actions: PairDecisionActions
  draft: DecisionDraft
}

/** "Same tattoo" (copper, the primary) or "Different tattoo", with its keyboard hint. */
function DecisionButton({
  decision,
  onDecide,
  busy,
}: {
  decision: 'same' | 'different'
  onDecide: (decision: TattooDecision) => void
  /** A decision is on its way: this one waits. */
  busy?: boolean
}) {
  const { t } = useTranslation()
  const same = decision === 'same'
  const Icon = same ? Check : X
  return (
    <Button
      variant={same ? 'solid' : 'outline'}
      aria-keyshortcuts={DECISION_KEYS[decision].join(' ').toUpperCase()}
      onClick={() => onDecide(decision)}
      disabled={busy}
      className={cn('w-full justify-between font-semibold', same && ON_PRIMARY)}
    >
      <span className="flex items-center gap-2">
        <Icon aria-hidden />
        {t(`humint.tattooMatch.decision.${decision}`)}
      </span>
      <KeyHint keys={DECISION_KEYS[decision]} />
    </Button>
  )
}

/** Where a typed letter belongs to a field or to an open overlay's own keys, never to a decision. */
const TYPING = new Set(['INPUT', 'TEXTAREA', 'SELECT'])
const OVERLAY = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'

/** The decision a key press asks for: S / J or D / K pressed alone, outside fields and overlays. */
function keyDecision(event: KeyboardEvent): TattooDecision | null {
  if (event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey)
    return null
  const { target } = event
  // A slider (the seek bars of the voice comparison) is an input a letter means nothing to.
  const slider = target instanceof HTMLInputElement && target.type === 'range'
  if (
    target instanceof HTMLElement &&
    !slider &&
    (TYPING.has(target.tagName) || target.isContentEditable)
  )
    return null
  if (target instanceof Element && target.closest(OVERLAY)) return null
  const key = event.key.toLowerCase()
  return (
    (Object.keys(DECISION_KEYS) as TattooDecision[]).find(decision =>
      DECISION_KEYS[decision].includes(key)
    ) ?? null
  )
}

/**
 * The S / D keys, listened to on the whole page: picking another pair or deciding one takes the
 * focus away with the control that had it, and the keys keep working. They act only while the
 * form is laid out — the shell's desktop breakpoint, the same gate as the buttons, so the phone
 * layout ignores them — and while no decision is on its way.
 */
export function useDecisionKeys(
  form: RefObject<HTMLElement | null>,
  busy: boolean | undefined,
  decide: (decision: TattooDecision) => void
) {
  const onKeyDown = useStableCallback((event: KeyboardEvent) => {
    const shown = (form.current?.getClientRects().length ?? 0) > 0
    const decision = shown && !busy ? keyDecision(event) : null
    if (!decision) return
    event.preventDefault()
    decide(decision)
  })
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])
}

function DecideForm({ pair, owners, actions, draft }: DecideFormProps) {
  const { t } = useTranslation()
  const noteId = useId()
  const formRef = useRef<HTMLElement>(null)
  const decide = (decision: TattooDecision) =>
    actions.onDecide(pair.id, decision, draft.note.trim())
  useDecisionKeys(formRef, actions.busy, decide)
  return (
    <section ref={formRef} aria-labelledby={`${noteId}-h`} className="flex flex-col gap-3">
      <h3 id={`${noteId}-h`} className={MICRO_LABEL}>
        {t('humint.tattooMatch.decision.heading')}
      </h3>
      <DecisionButton decision="same" onDecide={decide} busy={actions.busy} />
      <DecisionButton decision="different" onDecide={decide} busy={actions.busy} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={noteId} className="text-xs font-medium">
          {t('humint.tattooMatch.decision.note')}
          <span className="font-normal text-muted-foreground">({t('humint.common.optional')})</span>
        </Label>
        <Textarea
          id={noteId}
          rows={2}
          maxLength={NOTE_MAX_LENGTH}
          value={draft.note}
          onChange={event => draft.onNoteChange(event.target.value)}
          className="min-h-16 resize-none rounded-xl text-sm"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {t(decideHintKey(pair, owners), { tag: subjectTag(owners.candidate) })}
      </p>
    </section>
  )
}

function decideHintKey(pair: TattooPair, owners: PairDecisionPanelProps['owners']): string {
  if (owners.query.id === owners.candidate.id) return 'humint.tattooMatch.decision.hintSameSubject'
  if (pair.seenTogether) return 'humint.tattooMatch.decision.hintSeenTogether'
  return 'humint.tattooMatch.decision.hintMerge'
}

export function KeyHint({ keys }: { keys: readonly string[] }) {
  return (
    <span aria-hidden className="flex gap-1">
      {keys.map(key => (
        <kbd
          key={key}
          className={cn(
            MONO,
            'rounded-md border border-current/35 px-1.5 text-2xs leading-5 font-semibold uppercase'
          )}
        >
          {key}
        </kbd>
      ))}
    </span>
  )
}

interface MergeProposalProps {
  pairId: string
  owners: PairDecisionPanelProps['owners']
  actions: PairDecisionActions
}

/** A full-width answer whose label wraps inside the narrow panel instead of running out of it. */
const PROPOSAL_ACTION = 'h-auto min-h-10 w-full px-3 py-2 text-left whitespace-normal'

/** "The tattoo matches ZA-0157. Is it the same person?" Warning border: a lead, never a verdict. */
function MergeProposal({ pairId, owners, actions }: MergeProposalProps) {
  const { t } = useTranslation()
  const headingId = useId()
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-warning-border bg-card p-4',
        ENTER_UP
      )}
    >
      <h3 id={headingId} className="flex items-start gap-2 text-sm font-semibold">
        <GitMerge aria-hidden className="mt-0.5 size-4 shrink-0 text-warning-foreground" />
        <span>
          <Trans
            i18nKey="humint.tattooMatch.merge.question"
            values={{ tag: subjectTag(owners.candidate) }}
            components={{ tag: <span className={MONO} /> }}
          />
        </span>
      </h3>
      <p className="text-xs text-muted-foreground">
        {t('humint.tattooMatch.merge.explain', { tag: subjectTag(owners.query) })}
      </p>
      <Button
        variant="solid"
        onClick={() => actions.onMerge(pairId)}
        disabled={actions.busy}
        className={cn(PROPOSAL_ACTION, 'font-semibold', ON_PRIMARY)}
      >
        <GitMerge aria-hidden />
        {t('humint.tattooMatch.merge.merge')}
      </Button>
      <Button
        variant="outline"
        onClick={() => actions.onMotifOnly(pairId)}
        disabled={actions.busy}
        className={PROPOSAL_ACTION}
      >
        <Users aria-hidden />
        {t('humint.tattooMatch.merge.motifOnly')}
      </Button>
      <ReopenButton
        onReopen={() => actions.onReopen(pairId)}
        busy={actions.busy}
        className="self-start"
      />
    </section>
  )
}

function DecidedLine({
  outcome,
  onReopen,
  busy,
}: {
  outcome: PairOutcome
  /** Absent when the pair can never wait again (one subject holds both marks). */
  onReopen?: () => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  const Icon = OUTCOME_ICON[outcome]
  return (
    <div
      className={cn('flex flex-col gap-2 rounded-xl border border-border bg-card p-3', ENTER_UP)}
    >
      <span className="flex items-start gap-2 text-sm font-medium">
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        {t(`humint.tattooMatch.outcome.${outcome}`)}
      </span>
      {onReopen && <ReopenButton onReopen={onReopen} busy={busy} className="self-start" />}
    </div>
  )
}

function ReopenButton({
  onReopen,
  busy,
  className,
}: {
  onReopen: () => void
  busy?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onReopen}
      disabled={busy}
      className={cn('h-12 rounded-xl px-4 @5xl/humint:h-8 @5xl/humint:px-3', className)}
    >
      <RotateCcw aria-hidden />
      {t('humint.tattooMatch.decision.reopen')}
    </Button>
  )
}
